import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useMutation } from '@apollo/client';

import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';

import {
    Body1,
    Body2,
    GridItemRow,
    ModalActions,
    ModalContent,
    ModalTitle,
    PrimaryButton,
    SecondaryButton,
} from '../blocks';
import { MODALS } from '../constants';
import { PlanningDataContext, PlanningUiContext } from '../context';
import { UPDATE_ORDERS } from '../graphql/mutations';
import { useResourcesById } from '../hooks/useResourcesById';
import { useAssignToRoute } from '../map/useAssignToRoute';
import {
    addAllBatches,
    addRunSummary,
    assignInSequence,
    findOrdersOffResource,
    resourceName,
    selectionKey,
    targetRouteName,
} from '../util/ordersOffResource';

// Per-row, because a mixed set is the normal case, plus Add All for when every row just wants its
// resource's route.
const OrdersOffResourceModal = () => {
    // The granular contexts, not PlanningContext: this is always mounted, and the legacy value ticks
    // on every selection click and keystroke.
    const { modalOpen, setModalOpen, setError, setNotification } = useContext(PlanningUiContext);
    const {
        deliveryDate,
        state: { orders, routes, loading },
    } = useContext(PlanningDataContext);
    const resourcesById = useResourcesById();
    const { assign, assignLoading } = useAssignToRoute();
    const [updateOrders, { loading: updateLoading }] = useMutation(UPDATE_ORDERS, {
        onError: (error) => setError(error, "Error updating those orders' resource"),
    });
    // The order ids a run of adds was asked to place, from the click until its summary is shown.
    const [addRun, setAddRun] = useState(null);

    // Each batch after the first starts from a later render, so it calls the assign() that render
    // made rather than the one Add All was clicked with.
    const assignRef = useRef(assign);
    assignRef.current = assign;

    // `routes`, not `allRoutes`: shells included — a resource whose route was deleted gets one back
    // while orders await it — and deleted routes left out.
    const entries = useMemo(
        () => findOrdersOffResource({ orders, routes, deliveryDate, resourcesById }),
        [orders, routes, deliveryDate, resourcesById]
    );

    // Once per plan date, never on top of another dialog. Worth flagging, not nagging.
    const shownForDate = useRef(null);

    useEffect(() => {
        if (loading || modalOpen || entries.length === 0 || shownForDate.current === deliveryDate) {
            return;
        }

        shownForDate.current = deliveryDate;
        setModalOpen(MODALS.ORDERS_OFF_RESOURCE);
    }, [loading, modalOpen, entries.length, deliveryDate, setModalOpen]);

    // Read on the render the run finishes in: each batch settles only after its result is in the
    // cache, so rows that landed have already dropped out of `entries`.
    useEffect(() => {
        if (!addRun?.done) {
            return;
        }

        setAddRun(null);
        if (addRun.threw) {
            return;
        }

        const summary = addRunSummary({
            batchCount: addRun.batchCount,
            requested: addRun.orderIds.length,
            stillListed: entries.filter((entry) => addRun.orderIds.includes(entry.order.order_id)).length,
        });
        if (summary) {
            setNotification(summary);
        }
    }, [addRun, entries, setNotification]);

    const handleClose = () => setModalOpen(null);

    // Per-row adds and Add All share one run, and nothing starts while one is in flight: assign() is
    // a full-replace write, so two at once can clobber each other's stops.
    const runAdds = (batches) => {
        if (batches.length === 0) {
            return;
        }

        setAddRun({
            batchCount: batches.length,
            orderIds: batches.flatMap((batch) => batch.orderIds),
            done: false,
        });
        assignInSequence(
            (...args) => assignRef.current(...args),
            batches,
            (errors) => {
                if (errors.length > 0) {
                    setError(errors[0], 'Error adding orders to their route');
                }
                setAddRun((run) => run && { ...run, done: true, threw: errors.length > 0 });
            }
        );
    };

    // Rows that land drop out of `entries` as the cache updates; anything refused stays listed, with
    // assign()'s own notification saying why.
    const handleAdd = (entry) => runAdds(addAllBatches([entry]));
    const handleAddAll = () => runAdds(addAllBatches(entries));

    // Clearing, or moving the order onto the resource of the route already carrying it — either way
    // the load is metered against the resource it actually rides.
    const setOrderResource = async (entry, resourceId, message) => {
        // The hook's onError means Apollo resolves rather than rejects, so a failure arrives as
        // `errors` on the result.
        const { errors } = await updateOrders({
            variables: { order_ids: [entry.order.order_id], update: { resource_id: resourceId } },
        });

        if (!errors?.length) {
            setNotification({ severity: 'success', message });
        }
    };

    const handleClear = (entry) =>
        setOrderResource(entry, null, `Cleared the resource from order ${entry.order.order_number}.`);

    const handleMove = (entry, resource) =>
        setOrderResource(
            entry,
            resource.resource_id,
            `Moved order ${entry.order.order_number} to ${resource.name || "its route's resource"}.`
        );

    if (entries.length === 0) {
        return null;
    }

    const busy = assignLoading || updateLoading || !!addRun;
    const addableCount = entries.filter((entry) => entry.targetRoute).length;

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ORDERS_OFF_RESOURCE} onClose={handleClose}>
            <GridItemRow>
                <ModalTitle>Orders not on their resource's route</ModalTitle>
                <PrimaryButton
                    disabled={busy || addableCount === 0}
                    onClick={handleAddAll}
                    css={css`
                        margin-right: 1rem;
                        white-space: nowrap;
                    `}
                >
                    {addRun?.batchCount > 1 ? 'Adding…' : `Add All (${addableCount})`}
                </PrimaryButton>
            </GridItemRow>
            <ModalContent>
                <GridItemRow>
                    <Alert severity="warning">
                        {`${entries.length} order${entries.length > 1 ? 's are' : ' is'} assigned to a resource but
                        ${entries.length > 1 ? 'are' : 'is'} not on that resource's route for this day. ${
                            entries.length > 1 ? 'Their' : 'Its'
                        } weight and cubes still count against the resource's capacity.`}
                    </Alert>
                </GridItemRow>

                {entries.map((entry) => {
                    const target = targetRouteName(entry);
                    // Offered only when that route has a resource of its own; otherwise clearing is right.
                    const carrier = entry.currentRoute?.resource_id
                        ? resourcesById[entry.currentRoute.resource_id] || entry.currentRoute.resource
                        : null;

                    return (
                        <GridItemRow
                            key={entry.order.order_id}
                            css={css`
                                align-items: center;
                                flex-wrap: nowrap;
                                gap: 12px;
                                padding: 10px 0;
                                border-bottom: 1px solid #e2e2e2;
                            `}
                        >
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    min-width: 0;
                                `}
                            >
                                <Body1>{entry.order.order_number}</Body1>
                                <Body2>
                                    {resourceName(entry)}
                                    {target ? ` · route ${target}` : ' · not scheduled this day'}
                                </Body2>
                            </Grid>
                            <PrimaryButton
                                disabled={busy || !entry.targetRoute}
                                onClick={() => handleAdd(entry)}
                                css={css`
                                    max-width: 150px;
                                `}
                            >
                                Add to route
                            </PrimaryButton>
                            {carrier ? (
                                <SecondaryButton
                                    disabled={busy}
                                    onClick={() => handleMove(entry, carrier)}
                                    css={css`
                                        max-width: 150px;
                                    `}
                                >
                                    {`Move to ${carrier.name || "its route's resource"}`}
                                </SecondaryButton>
                            ) : (
                                <SecondaryButton
                                    disabled={busy}
                                    onClick={() => handleClear(entry)}
                                    css={css`
                                        max-width: 150px;
                                    `}
                                >
                                    Clear resource
                                </SecondaryButton>
                            )}
                        </GridItemRow>
                    );
                })}
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={busy} onClick={handleClose}>
                    Close
                </SecondaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default OrdersOffResourceModal;
